import React, { useState, useEffect } from 'react';
import { ProductShowcase, restaurantShowcaseItems } from './productShowCase';
import { Gallery, restaurantProducts } from './Gallery';
import { CuratedCollections, restaurantCollections } from './CuratedCollections';
import ScrollingSection from './ScrollingSection';
import ScroollToTop from './ScroollToTop';
import Contact from './Contact';

const restaurantCategories = ['Fine Dining', 'Cafe', 'Bistro', 'Outdoor'];

// Contact Modal Component
const ContactModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white shadow-3xl w-full max-w-2xl max-h-[90vh] sm:max-h-[95vh] overflow-y-auto relative rounded-lg">
        <button
          className="absolute top-2 right-3 sm:top-3 sm:right-4 text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold z-[9999]"
          aria-label="Close modal"
          onClick={onClose}
        >
          &times;
        </button>
        <Contact />
      </div>
    </div>
  );
};

const Restaurant = () => {
  const [showModal, setShowModal] = useState(false);

  // Preload hero image
  useEffect(() => {
    const img = new Image();
    img.src = "/assets/caf_fd_2.jpg";
  }, []);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  return (
    <div>
      <ScroollToTop />

      {/* Hero Section */}
      <header
        className="relative bg-cover bg-center h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[80vh]"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&w=1920')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 md:px-8 lg:px-16 z-10">
          <div className="w-full md:w-2/3 lg:w-1/2 text-white">
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight mb-4">
              Restaurant Collection For Modern Living
            </h1>
            <p className="font-heading text-sm sm:text-base md:text-lg lg:text-xl mb-6">
              Transform your restaurant into a sanctuary of comfort and style
              with our premium furniture collection designed for relaxation
              and rejuvenation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg">
                Explore the Collection
              </button>
              <button onClick={openModal} className="border-2 border-white hover:bg-white hover:text-gray-900 font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105">
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Product Showcase */}
      <ProductShowcase
        title="Restaurant Furniture Collections"
        items={restaurantShowcaseItems}
      />

      {/* Signature Collection */}
      <section className="bg-gray-300 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl sm:text-3xl font-semibold mb-6 sm:mb-8">Signature Collection</h2>
          <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl shadow-md flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
            <div className="w-full lg:w-1/3 relative overflow-hidden bg-gray-100 rounded-lg shadow-md">
              <div className="aspect-[4/5] w-full">
                <img
                  src="/assets/caf_fd_1.jpg"
                  alt="signature collection img"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { 
                    e.target.onerror = null; 
                    e.target.src = "https://placehold.co/600x750/E5E7EB/6B7280?text=Restaurant+Design"; 
                  }}
                />
              </div>
            </div>
            <div className="w-full lg:w-2/3">
              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Modern Design Of restaurant</h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">
                Our flagship dining chair combines traditional craftsmanship with modern design sensibilities. Handcrafted from sustainably sourced oak with premium upholstery,
                it offers exceptional comfort for extended dining experiences while maintaining a sophisticated profile that elevates any restaurant interior.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
                <button className="bg-yellow-700 hover:bg-yellow-800 text-white py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base rounded transition-colors">
                  View Details
                </button>
                <button onClick={openModal} className="bg-gray-700 hover:bg-gray-800 text-white py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base rounded transition-colors">
                  Request a Quote
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                <span className="text-gray-700 font-semibold">5+ Finishes</span>
                <span className="text-gray-700 font-semibold">10-Year Warranty</span>
                <span className="text-gray-700 font-semibold">100% Sustainable</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-white py-8 sm:py-12 min-h-[600px] sm:min-h-[800px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-6 sm:mb-8 text-center">Restaurant Gallery</h2>
          <div className="min-h-[500px] sm:min-h-[600px]">
            <Gallery
              products={restaurantProducts}
              categories={restaurantCategories}
              autoCycle={true}
              cycleInterval={2000}
            />
          </div>
        </div>
      </section>

      {/* Scrolling Section */}
      <section>
        <ScrollingSection
          sectionContent={
            <section className="relative z-10 bg-black/80 p-4 sm:p-6 md:p-8 lg:p-12 xl:p-16 w-full min-h-screen flex items-center justify-center">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
                  <div className="space-y-4 sm:space-y-6 text-left">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-white leading-tight">Custom Restaurant Furniture Solutions</h2>
                    <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                      We understand that every restaurant has unique needs and
                      aesthetic requirements. Our custom furniture solutions are
                      designed to meet your specific requirements, from bespoke
                      booth seating to branded furniture elements.
                    </p>
                    <div className="space-y-3 sm:space-y-4">
                      {/* Custom Design Services */}
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-md bg-gray-100 text-white">
                            <i className="fas fa-pencil-ruler"></i>
                          </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <h3 className="text-base sm:text-lg font-serif text-gray-200">Custom Design Services</h3>
                          <p className="mt-1 sm:mt-2 text-gray-300 text-sm sm:text-base">
                            Work with our design team to create furniture that perfectly matches your restaurant's concept and brand.
                          </p>
                        </div>
                      </div>

                      {/* Space Planning */}
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-md bg-gray-100 text-white">
                            <i className="fas fa-ruler-combined"></i>
                          </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <h3 className="text-base sm:text-lg font-serif text-gray-200">Space Planning</h3>
                          <p className="mt-1 sm:mt-2 text-gray-300 text-sm sm:text-base">
                            Maximize your restaurant's seating capacity and flow with our professional space planning services.
                          </p>
                        </div>
                      </div>

                      {/* Material & Finish Selection */}
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-md bg-gray-100 text-white">
                            <i className="fas fa-palette"></i>
                          </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <h3 className="text-base sm:text-lg font-serif text-gray-200">Material & Finish Selection</h3>
                          <p className="mt-1 sm:mt-2 text-gray-300 text-sm sm:text-base">
                            Choose from a wide range of commercial-grade materials, finishes, and upholstery options.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 sm:mt-6 flex justify-center md:justify-start">
                      <button className="inline-flex items-center px-5 py-2.5 sm:px-6 sm:py-3 border border-transparent text-sm sm:text-base font-serif rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 cursor-pointer whitespace-nowrap">
                        Request Custom Quote
                      </button>
                    </div>
                  </div>

                  <div className="relative h-64 sm:h-80 md:h-96 rounded-xl overflow-hidden shadow-xl">
                    <img
                      src="/assets/caf_fd_3.jpg"
                      alt="Custom furniture design process"
                      className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
              </div>
            </section>
          }
          imageUrl="/assets/slide4.png"
        />
      </section>

      {/* Curated Collections */}
      <CuratedCollections
        title="Curated Restaurant Styles"
        description="Discover cohesive furniture sets and decor ideas for your home's heart."
        collections={restaurantCollections}
      />

      {/* Contact Modal */}
      <ContactModal isOpen={showModal} onClose={closeModal} />

    </div>
  );
};

export default Restaurant;